import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut, Users, Eye, Mail, Flame, RefreshCcw, ChevronRight, FileText,
  Cookie, Sparkles, MessageSquare, Inbox, LayoutDashboard,
} from "lucide-react";
import { adminGet, logout, isLoggedIn } from "../lib/adminAuth";
import SessionDetailModal from "../components/admin/SessionDetailModal";
import ChatDetailModal from "../components/admin/ChatDetailModal";

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-[hsl(var(--bg))] border border-[hsl(var(--line))] p-6">
      <div className="flex items-center gap-3 kicker">
        <Icon size={14} className="text-[hsl(var(--accent))]" />
        {label}
      </div>
      <div className="font-display text-4xl md:text-5xl mt-3">{value}</div>
      {sub && <div className="text-xs text-[hsl(var(--ink-soft))] mt-2">{sub}</div>}
    </div>
  );
}

function Panel({ title, children, right }) {
  return (
    <section className="bg-[hsl(var(--bg))] border border-[hsl(var(--line))] p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="kicker">{title}</div>
        {right}
      </div>
      {children}
    </section>
  );
}

function humanDuration(sec) {
  if (!sec || sec < 0) return "0s";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m ? `${m}m ${s}s` : `${s}s`;
}

function shortUA(ua = "") {
  if (/iPhone/i.test(ua)) return "iPhone";
  if (/iPad/i.test(ua)) return "iPad";
  if (/Android/i.test(ua)) return "Android";
  if (/Mac OS X/i.test(ua)) return "Mac";
  if (/Windows/i.test(ua)) return "Windows";
  if (/Linux/i.test(ua)) return "Linux";
  return "Other";
}

function exportCsv(rows, filename) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => escape(r[h])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function TabButton({ active, onClick, icon: Icon, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-2 px-4 py-3 text-[11px] tracking-[0.3em] uppercase border-b-2 transition-colors ${
        active
          ? "border-[hsl(var(--accent))] text-[hsl(var(--ink))]"
          : "border-transparent text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--ink))]"
      }`}
    >
      {Icon && <Icon size={14} />}
      {children}
    </button>
  );
}

function OverviewTab({ summary, hotLeads, topPages, referrers, sessions, openDetail }) {
  return (
    <>
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Users} label="Visitors today" value={summary.sessions.today} sub={`${summary.sessions.week} this week`} />
          <StatCard icon={Eye} label="Page views today" value={summary.pageviews.today} sub={`${summary.pageviews.week} this week`} />
          <StatCard icon={Mail} label="Enquiries this week" value={summary.enquiries.week} sub={`${summary.enquiries.total} total`} />
          <StatCard icon={Flame} label="Hot lead threshold" value={`${summary.hot_lead_threshold}+`} sub="score points" />
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6 mt-8">
        <Panel title={`Hot leads (score ≥ ${summary?.hot_lead_threshold || 8})`}>
          {hotLeads.length === 0 ? (
            <div className="text-sm text-[hsl(var(--ink-soft))]">No hot leads yet.</div>
          ) : (
            <ul className="divide-y divide-[hsl(var(--line))]">
              {hotLeads.map((s) => (
                <li key={s.session_id}>
                  <button onClick={() => openDetail(s.session_id)} className="w-full text-left py-3 flex items-center justify-between hover:bg-[hsl(var(--bg-alt))] px-2">
                    <div>
                      <div className="font-display text-lg flex items-center gap-2">
                        <Flame size={14} className="text-[hsl(var(--accent))]" />
                        Score {s.score}
                      </div>
                      <div className="text-xs text-[hsl(var(--ink-soft))]">
                        {s.page_count} pages · {humanDuration(s.duration_seconds)} · {s.city || "—"} {s.country ? `· ${s.country}` : ""}
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-[hsl(var(--ink-soft))]" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Top pages (last 30 days)">
          {topPages.length === 0 ? (
            <div className="text-sm text-[hsl(var(--ink-soft))]">No pageviews yet.</div>
          ) : (
            <ul className="space-y-2">
              {topPages.map((p) => (
                <li key={p.path} className="flex items-center justify-between text-sm">
                  <span className="font-display text-base">{p.path}</span>
                  <span className="text-[hsl(var(--ink-soft))]">{p.count}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <Panel title="Top referrers (last 30 days)">
          {referrers.length === 0 ? (
            <div className="text-sm text-[hsl(var(--ink-soft))]">No data yet.</div>
          ) : (
            <ul className="space-y-2">
              {referrers.map((r) => (
                <li key={r.referrer} className="flex items-center justify-between text-sm">
                  <span className="font-display text-base truncate max-w-[70%]">{r.referrer || "(direct)"}</span>
                  <span className="text-[hsl(var(--ink-soft))]">{r.count}</span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Recent sessions">
          {sessions.length === 0 ? (
            <div className="text-sm text-[hsl(var(--ink-soft))]">No sessions yet.</div>
          ) : (
            <ul className="divide-y divide-[hsl(var(--line))]">
              {sessions.slice(0, 8).map((s) => (
                <li key={s.session_id}>
                  <button onClick={() => openDetail(s.session_id)} className="w-full text-left py-3 flex items-center justify-between hover:bg-[hsl(var(--bg-alt))] px-2">
                    <div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className={`inline-block h-2 w-2 rounded-full ${s.is_hot ? "bg-[hsl(var(--accent))]" : "bg-[hsl(var(--ink-soft))]"}`} />
                        <span className="font-display text-base">Score {s.score}</span>
                        <span className="text-xs text-[hsl(var(--ink-soft))]">· {shortUA(s.user_agent)}</span>
                      </div>
                      <div className="text-xs text-[hsl(var(--ink-soft))] mt-1">
                        {s.page_count} pages · {humanDuration(s.duration_seconds)} · {s.city || "—"} {s.country ? `· ${s.country}` : ""}
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-[hsl(var(--ink-soft))]" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </>
  );
}

function CapturedAllTab({ enquiries, moodBoards, chats, consents }) {
  const combined = [
    // Exclude enquiries auto-created from Vision Studio saves — they're re-added below as "Vision Studio Save"
    ...enquiries
      .filter((e) => e.source !== "mood_board")
      .map((e) => ({
        when: e.timestamp,
        source: "Enquiry",
        name: e.name,
        email: e.email,
        detail: `${e.event_type || "—"} · ${e.message?.slice(0, 80) || "—"}`,
        key: `enq-${e.id}`,
      })),
    ...moodBoards.filter((m) => m.saved && m.saved_by).map((m) => ({
      when: m.saved_at || m.created_at,
      source: "Vision Studio Save",
      name: m.saved_by?.name || "—",
      email: m.saved_by?.email || "—",
      detail: `${m.inputs?.event_type || "—"} · ${m.inputs?.colors || ""} · ${m.inputs?.style || ""}`,
      key: `mb-${m.id}`,
    })),
    ...chats.filter((c) => c.escalated).map((c) => ({
      when: c.last_updated,
      source: "Chat Escalated",
      name: c.escalation_name || "—",
      email: c.escalation_email || "—",
      detail: `${c.message_count} msgs · ${(c.first_message || "").slice(0, 80)}`,
      key: `chat-${c.conversation_id}`,
    })),
    ...consents.filter((c) => c.choice === "accepted").map((c) => ({
      when: c.timestamp,
      source: "Consent",
      name: "—",
      email: "—",
      detail: `${c.choice} · ${c.page || "/"} · ${c.ip}`,
      key: `con-${c.id}`,
    })),
  ].sort((a, b) => new Date(b.when) - new Date(a.when));

  return (
    <Panel
      title={`All captured details (${combined.length})`}
      right={
        <button
          onClick={() => exportCsv(combined, `captured-${Date.now()}.csv`)}
          disabled={combined.length === 0}
          className="text-xs tracking-[0.3em] uppercase link-underline disabled:opacity-40"
          data-testid="export-captured-csv"
        >
          Export CSV
        </button>
      }
    >
      {combined.length === 0 ? (
        <div className="text-sm text-[hsl(var(--ink-soft))]">No captured details yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left kicker">
              <tr className="border-b border-[hsl(var(--line))]">
                <th className="py-3 pr-4">When</th>
                <th className="py-3 pr-4">Source</th>
                <th className="py-3 pr-4">Name</th>
                <th className="py-3 pr-4">Email</th>
                <th className="py-3 pr-4">Detail</th>
              </tr>
            </thead>
            <tbody data-testid="all-leads-table">
              {combined.map((r) => (
                <tr key={r.key} className="border-b border-[hsl(var(--line))] hover:bg-[hsl(var(--bg-alt))]">
                  <td className="py-3 pr-4 text-[hsl(var(--ink-soft))] whitespace-nowrap">{new Date(r.when).toLocaleString()}</td>
                  <td className="py-3 pr-4">
                    <span className="text-[10px] tracking-[0.3em] uppercase text-[hsl(var(--accent))]">{r.source}</span>
                  </td>
                  <td className="py-3 pr-4 font-display text-base">{r.name}</td>
                  <td className="py-3 pr-4">
                    {r.email && r.email !== "—" ? (
                      <a href={`mailto:${r.email}`} className="text-[hsl(var(--accent))] underline">{r.email}</a>
                    ) : "—"}
                  </td>
                  <td className="py-3 pr-4 text-[hsl(var(--ink-soft))] max-w-[420px] truncate">{r.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function EnquiriesTab({ enquiries }) {
  return (
    <Panel
      title={`Enquiries (${enquiries.length})`}
      right={
        <button
          onClick={() => exportCsv(enquiries, `enquiries-${Date.now()}.csv`)}
          disabled={enquiries.length === 0}
          className="text-xs tracking-[0.3em] uppercase link-underline disabled:opacity-40"
        >
          Export CSV
        </button>
      }
    >
      {enquiries.length === 0 ? (
        <div className="text-sm text-[hsl(var(--ink-soft))]">No enquiries yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="enquiries-table">
            <thead className="text-left kicker">
              <tr className="border-b border-[hsl(var(--line))]">
                <th className="py-3 pr-4">When</th>
                <th className="py-3 pr-4">Name</th>
                <th className="py-3 pr-4">Email</th>
                <th className="py-3 pr-4">Event</th>
                <th className="py-3 pr-4">Date</th>
                <th className="py-3 pr-4">Guests</th>
                <th className="py-3 pr-4">Budget</th>
                <th className="py-3 pr-4">Source</th>
              </tr>
            </thead>
            <tbody>
              {enquiries.map((r) => (
                <tr key={r.id} className="border-b border-[hsl(var(--line))] hover:bg-[hsl(var(--bg-alt))]">
                  <td className="py-3 pr-4 text-[hsl(var(--ink-soft))] whitespace-nowrap">{new Date(r.timestamp).toLocaleString()}</td>
                  <td className="py-3 pr-4 font-display text-base">{r.name}</td>
                  <td className="py-3 pr-4">
                    <a href={`mailto:${r.email}`} className="text-[hsl(var(--accent))] underline">{r.email}</a>
                  </td>
                  <td className="py-3 pr-4">{r.event_type || "—"}</td>
                  <td className="py-3 pr-4">{r.preferred_date || "—"}</td>
                  <td className="py-3 pr-4">{r.guests || "—"}</td>
                  <td className="py-3 pr-4">{r.budget || "—"}</td>
                  <td className="py-3 pr-4 text-[hsl(var(--ink-soft))]">{r.source || "form"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function MoodBoardsTab({ moodBoards }) {
  const rows = moodBoards.filter((m) => m.saved && m.saved_by);
  return (
    <Panel
      title={`Mood board leads (${rows.length}) · Total generated: ${moodBoards.length}`}
      right={
        <button
          onClick={() =>
            exportCsv(
              rows.map((m) => ({
                saved_at: m.saved_at,
                name: m.saved_by?.name,
                email: m.saved_by?.email,
                note: m.saved_by?.note,
                event_type: m.inputs?.event_type,
                colors: m.inputs?.colors,
                style: m.inputs?.style,
                freeform: m.inputs?.freeform,
                caption: m.caption,
                board_id: m.id,
              })),
              `mood-board-leads-${Date.now()}.csv`,
            )
          }
          disabled={rows.length === 0}
          className="text-xs tracking-[0.3em] uppercase link-underline disabled:opacity-40"
        >
          Export CSV
        </button>
      }
    >
      {rows.length === 0 ? (
        <div className="text-sm text-[hsl(var(--ink-soft))]">
          No saved vision boards yet. Visitors who save a board (email required) will appear here.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="mood-board-leads-table">
            <thead className="text-left kicker">
              <tr className="border-b border-[hsl(var(--line))]">
                <th className="py-3 pr-4">Saved</th>
                <th className="py-3 pr-4">Name</th>
                <th className="py-3 pr-4">Email</th>
                <th className="py-3 pr-4">Event</th>
                <th className="py-3 pr-4">Palette / Style</th>
                <th className="py-3 pr-4">Vibe caption</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr key={m.id} className="border-b border-[hsl(var(--line))] hover:bg-[hsl(var(--bg-alt))]">
                  <td className="py-3 pr-4 text-[hsl(var(--ink-soft))] whitespace-nowrap">{new Date(m.saved_at || m.created_at).toLocaleString()}</td>
                  <td className="py-3 pr-4 font-display text-base">{m.saved_by?.name || "—"}</td>
                  <td className="py-3 pr-4">
                    <a href={`mailto:${m.saved_by?.email}`} className="text-[hsl(var(--accent))] underline">{m.saved_by?.email}</a>
                  </td>
                  <td className="py-3 pr-4">{m.inputs?.event_type || "—"}</td>
                  <td className="py-3 pr-4 text-xs">{m.inputs?.colors} · {m.inputs?.style}</td>
                  <td className="py-3 pr-4 text-[hsl(var(--ink-soft))] max-w-[420px] truncate italic">{m.caption || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

function ChatsTab({ chats, openChatDetail }) {
  return (
    <Panel title={`Chat conversations (${chats.length})`}>
      {chats.length === 0 ? (
        <div className="text-sm text-[hsl(var(--ink-soft))]">No chats yet.</div>
      ) : (
        <ul className="divide-y divide-[hsl(var(--line))]" data-testid="chats-list">
          {chats.map((c) => (
            <li key={c.conversation_id}>
              <button onClick={() => openChatDetail(c.conversation_id)} className="w-full text-left py-3 flex items-center justify-between hover:bg-[hsl(var(--bg-alt))] px-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    {c.escalated && <span className="text-[10px] tracking-[0.3em] uppercase text-[hsl(var(--accent))]">ESCALATED</span>}
                    <span className="font-display text-base">{c.escalation_name || `${c.message_count} messages`}</span>
                  </div>
                  <div className="text-xs text-[hsl(var(--ink-soft))] mt-1 truncate max-w-[600px]">
                    &ldquo;{c.first_message || "(no message)"}&rdquo;
                  </div>
                  <div className="text-xs text-[hsl(var(--ink-soft))] mt-1">{new Date(c.last_updated).toLocaleString()}</div>
                </div>
                <ChevronRight size={16} className="text-[hsl(var(--ink-soft))] flex-shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function SessionsTab({ sessions, openDetail }) {
  return (
    <Panel title={`Visitor sessions (${sessions.length})`}>
      {sessions.length === 0 ? (
        <div className="text-sm text-[hsl(var(--ink-soft))]">No sessions yet.</div>
      ) : (
        <ul className="divide-y divide-[hsl(var(--line))]" data-testid="sessions-list">
          {sessions.map((s) => (
            <li key={s.session_id}>
              <button onClick={() => openDetail(s.session_id)} className="w-full text-left py-3 flex items-center justify-between hover:bg-[hsl(var(--bg-alt))] px-2">
                <div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className={`inline-block h-2 w-2 rounded-full ${s.is_hot ? "bg-[hsl(var(--accent))]" : "bg-[hsl(var(--ink-soft))]"}`} />
                    <span className="font-display text-base">Score {s.score}</span>
                    <span className="text-xs text-[hsl(var(--ink-soft))]">· {shortUA(s.user_agent)}</span>
                  </div>
                  <div className="text-xs text-[hsl(var(--ink-soft))] mt-1">
                    {s.page_count} pages · {humanDuration(s.duration_seconds)} · {s.city || "—"} {s.country ? `· ${s.country}` : ""}
                  </div>
                </div>
                <ChevronRight size={16} className="text-[hsl(var(--ink-soft))]" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function ConsentsTab({ consents, consentSummary }) {
  return (
    <>
      {consentSummary && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <StatCard icon={Cookie} label="Total decisions" value={consentSummary.total} />
          <StatCard icon={Cookie} label="Accepted" value={consentSummary.accepted} sub={consentSummary.total ? `${Math.round((consentSummary.accepted / consentSummary.total) * 100)}% acceptance` : "—"} />
          <StatCard icon={Cookie} label="Rejected" value={consentSummary.rejected} sub={consentSummary.total ? `${Math.round((consentSummary.rejected / consentSummary.total) * 100)}% rejection` : "—"} />
        </div>
      )}
      <Panel
        title={`Consent records (${consents.length})`}
        right={
          <button
            onClick={() => exportCsv(consents, `consents-${Date.now()}.csv`)}
            disabled={consents.length === 0}
            className="text-xs tracking-[0.3em] uppercase link-underline disabled:opacity-40"
          >
            Export CSV
          </button>
        }
      >
        {consents.length === 0 ? (
          <div className="text-sm text-[hsl(var(--ink-soft))]">
            No consent records yet. They appear as soon as visitors click accept or reject on the cookie banner.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="consents-table">
              <thead className="text-left kicker">
                <tr className="border-b border-[hsl(var(--line))]">
                  <th className="py-3 pr-4">When</th>
                  <th className="py-3 pr-4">Choice</th>
                  <th className="py-3 pr-4">Page</th>
                  <th className="py-3 pr-4">IP</th>
                  <th className="py-3 pr-4">Device</th>
                  <th className="py-3 pr-4">Policy v.</th>
                </tr>
              </thead>
              <tbody>
                {consents.map((c) => (
                  <tr key={c.id} className="border-b border-[hsl(var(--line))] hover:bg-[hsl(var(--bg-alt))]">
                    <td className="py-3 pr-4 text-[hsl(var(--ink-soft))] whitespace-nowrap">{new Date(c.timestamp).toLocaleString()}</td>
                    <td className="py-3 pr-4">
                      <span className={`text-[10px] tracking-[0.3em] uppercase ${c.choice === "accepted" ? "text-[hsl(var(--accent))]" : "text-[hsl(var(--ink-soft))]"}`}>
                        {c.choice}
                      </span>
                    </td>
                    <td className="py-3 pr-4 font-mono text-xs">{c.page || "/"}</td>
                    <td className="py-3 pr-4 font-mono text-xs">{c.ip}</td>
                    <td className="py-3 pr-4">{shortUA(c.user_agent)}</td>
                    <td className="py-3 pr-4 text-[hsl(var(--ink-soft))]">{c.policy_version}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}

const CAPTURED_SUB_TABS = [
  { id: "all", label: "All", icon: Inbox },
  { id: "enquiries", label: "Enquiries", icon: Mail },
  { id: "mood", label: "Vision Boards", icon: Sparkles },
  { id: "chats", label: "Chats", icon: MessageSquare },
  { id: "sessions", label: "Sessions", icon: Users },
  { id: "consents", label: "Consents", icon: Cookie },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [topPages, setTopPages] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [hotLeads, setHotLeads] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [chats, setChats] = useState([]);
  const [moodBoards, setMoodBoards] = useState([]);
  const [consents, setConsents] = useState([]);
  const [consentSummary, setConsentSummary] = useState(null);
  const [chatDetail, setChatDetail] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [topTab, setTopTab] = useState("overview");
  const [subTab, setSubTab] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [s, p, r, h, se, e, ch, mb, co] = await Promise.all([
        adminGet("/admin/summary"),
        adminGet("/admin/top-pages"),
        adminGet("/admin/top-referrers"),
        adminGet("/admin/hot-leads"),
        adminGet("/admin/sessions?limit=100"),
        adminGet("/admin/enquiries"),
        adminGet("/admin/chats?limit=100"),
        adminGet("/admin/mood-boards?limit=100"),
        adminGet("/admin/consents?limit=500"),
      ]);
      setSummary(s);
      setTopPages(p.rows || []);
      setReferrers(r.rows || []);
      setHotLeads(h.rows || []);
      setSessions(se.rows || []);
      setEnquiries(e.rows || []);
      setChats(ch.rows || []);
      setMoodBoards(mb.rows || []);
      setConsents(co.rows || []);
      setConsentSummary(co.summary || null);
    } catch (err) {
      if (err.message === "unauthorized") {
        nav("/admin/login");
        return;
      }
      setError(err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [nav]);

  useEffect(() => {
    if (!isLoggedIn()) {
      nav("/admin/login");
      return;
    }
    load();
  }, [load, nav]);

  const openDetail = async (sessionId) => {
    try {
      const d = await adminGet(`/admin/session/${sessionId}`);
      setDetail(d);
    } catch (err) {
      setError(err.message);
    }
  };

  const openChatDetail = async (conversationId) => {
    try {
      const d = await adminGet(`/admin/chats/${conversationId}`);
      setChatDetail(d);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--bg-alt))] text-[hsl(var(--ink))]">
      <header className="bg-[hsl(var(--bg))] border-b border-[hsl(var(--line))]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="font-editorial text-2xl tracking-wider">
            SwS <span className="text-[hsl(var(--ink-soft))] text-sm tracking-[0.35em] uppercase ml-2">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/admin/quotations" className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase hover:text-[hsl(var(--accent))]">
              <FileText size={14} /> Quotations
            </Link>
            <Link to="/" className="text-xs tracking-[0.3em] uppercase hover:text-[hsl(var(--accent))]">View site</Link>
            <button onClick={load} className="text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--accent))]" title="Refresh">
              <RefreshCcw size={16} />
            </button>
            <button
              onClick={async () => { await logout(); nav("/admin/login"); }}
              className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--accent))]"
              data-testid="admin-signout"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        {/* Top-level tabs */}
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 flex items-center gap-2 border-t border-[hsl(var(--line))]">
          <TabButton active={topTab === "overview"} onClick={() => setTopTab("overview")} icon={LayoutDashboard} testId="tab-overview">
            Overview
          </TabButton>
          <TabButton active={topTab === "captured"} onClick={() => setTopTab("captured")} icon={Inbox} testId="tab-captured">
            Captured Details
          </TabButton>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 md:px-10 py-10 space-y-8">
        {error && (
          <div className="border border-[hsl(var(--accent))] text-[hsl(var(--accent))] p-4 text-sm">{error}</div>
        )}

        {loading && !summary ? (
          <div className="text-[hsl(var(--ink-soft))]">Loading…</div>
        ) : topTab === "overview" ? (
          <OverviewTab
            summary={summary}
            hotLeads={hotLeads}
            topPages={topPages}
            referrers={referrers}
            sessions={sessions}
            openDetail={openDetail}
          />
        ) : (
          <>
            {/* Sub-tabs for Captured Details */}
            <div className="flex flex-wrap gap-1 border-b border-[hsl(var(--line))] -mt-4">
              {CAPTURED_SUB_TABS.map((t) => (
                <TabButton
                  key={t.id}
                  active={subTab === t.id}
                  onClick={() => setSubTab(t.id)}
                  icon={t.icon}
                  testId={`subtab-${t.id}`}
                >
                  {t.label}
                </TabButton>
              ))}
            </div>

            {subTab === "all" && (
              <CapturedAllTab enquiries={enquiries} moodBoards={moodBoards} chats={chats} consents={consents} />
            )}
            {subTab === "enquiries" && <EnquiriesTab enquiries={enquiries} />}
            {subTab === "mood" && <MoodBoardsTab moodBoards={moodBoards} />}
            {subTab === "chats" && <ChatsTab chats={chats} openChatDetail={openChatDetail} />}
            {subTab === "sessions" && <SessionsTab sessions={sessions} openDetail={openDetail} />}
            {subTab === "consents" && <ConsentsTab consents={consents} consentSummary={consentSummary} />}
          </>
        )}
      </main>

      <ChatDetailModal chatDetail={chatDetail} onClose={() => setChatDetail(null)} />
      <SessionDetailModal detail={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
